import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaPlay, FaLock, FaFolder, FaCrown, FaCheck } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recetarios = () => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasContent, setHasContent] = useState(false);
  const [user, setUser] = useState(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState({ price_cop: 50000, price_usd: 12 });
  const [userAccess, setUserAccess] = useState({});

  useEffect(() => {
    checkContent();
    fetchFolders();
    fetchUser();
    fetchSubscriptionPrice();
  }, []);

  const checkContent = async () => {
    try {
      const response = await axios.get(`${API}/recipe-videos/has-content`);
      setHasContent(response.data.has_content);
    } catch (error) {
      console.error('Error checking content:', error);
      setHasContent(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/recipe-videos/folders`);
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: false });
      setUser(response.data);
      // Check access for each folder
      if (response.data?.id) {
        checkAllFolderAccess(response.data.id);
      }
    } catch (error) {
      setUser(null);
    }
  };

  const fetchSubscriptionPrice = async () => {
    try {
      const response = await axios.get(`${API}/recipe-videos/subscription-price`);
      setSubscriptionPrice(response.data);
    } catch (error) {
      console.error('Error fetching price:', error);
    }
  };

  const checkAllFolderAccess = async (userId) => {
    const accessMap = {};
    for (const folder of folders) {
      try {
        const response = await axios.get(`${API}/recipe-videos/check-access/${folder.id}?user_id=${userId}`);
        accessMap[folder.id] = response.data;
      } catch (error) {
        accessMap[folder.id] = { has_access: false };
      }
    }
    setUserAccess(accessMap);
  };

  useEffect(() => {
    if (user?.id && folders.length > 0) {
      checkAllFolderAccess(user.id);
    }
  }, [user?.id, folders.length]);

  const handleFolderClick = (folder) => {
    const access = userAccess[folder.id];
    if (access?.has_access || folder.is_free) {
      navigate(`/recetarios/folder/${folder.id}`);
    } else if (!user) {
      toast.info('Inicia sesión para acceder a este contenido');
      navigate('/login');
    } else {
      // Mostrar opción de compra
      toast.info(`Este contenido tiene un costo de $${folder.price?.toLocaleString('es-CO')} COP`);
    }
  };

  // Mostrar "Próximamente" si no hay contenido
  if (!loading && !hasContent) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-8 text-gray-400 hover:text-white"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            {/* Icono de construcción */}
            <div 
              className="w-32 h-32 mx-auto mb-8 rounded-full flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.2), rgba(0, 240, 255, 0.2))',
                border: '2px solid rgba(var(--brand-primary-glow), 0.3)'
              }}
            >
              <span className="text-6xl">🚧</span>
            </div>

            <h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Recetarios en Video
            </h1>

            <div 
              className="inline-block rounded-full px-6 py-2 mb-6"
              style={{ 
                background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
              }}
            >
              <span className="text-white font-bold text-lg">🔨 PRÓXIMAMENTE</span>
            </div>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Estamos preparando una colección increíble de recetas en video para ti. 
              <br />
              <span className="text-[#00F0FF]">¡Muy pronto podrás aprender a cocinar delicioso y saludable!</span>
            </p>

            <div 
              className="max-w-md mx-auto rounded-2xl p-6"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <h3 className="text-white font-semibold mb-4">Lo que vendrá:</h3>
              <ul className="text-gray-400 text-left space-y-2">
                <li className="flex items-center gap-2">
                  <FaPlay className="text-[var(--brand-primary)]" /> Videos paso a paso
                </li>
                <li className="flex items-center gap-2">
                  <FaFolder className="text-[#00F0FF]" /> Organizados por categorías
                </li>
                <li className="flex items-center gap-2">
                  <FaCrown className="text-yellow-500" /> Recetas exclusivas
                </li>
              </ul>
            </div>

            <Button 
              onClick={() => navigate('/dashboard')} 
              className="mt-8 rounded-full px-8 py-3 text-white font-bold"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              Volver al Dashboard
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando recetarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-6xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-8 text-gray-400 hover:text-white"
        >
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Recetarios en 
            <span 
              className="ml-2 bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              Video
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Aprende a cocinar delicioso y saludable con nuestros videos paso a paso
          </p>
        </motion.div>

        {/* Suscripción mensual */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 rounded-2xl p-6 text-center"
          style={{ 
            background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.15), rgba(0, 240, 255, 0.15))',
            border: '1px solid rgba(var(--brand-primary-glow), 0.3)'
          }}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <FaCrown className="text-yellow-500 text-xl" />
            <h3 className="text-xl font-bold text-white">Acceso Completo</h3>
          </div>
          <p className="text-gray-300 mb-4">
            Suscríbete y obtén acceso a <strong className="text-white">TODOS</strong> los videos de recetas
          </p>
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="text-3xl font-bold text-white">
              ${subscriptionPrice.price_cop?.toLocaleString('es-CO')} <span className="text-lg text-gray-400">COP/mes</span>
            </span>
            <span className="text-gray-500">|</span>
            <span className="text-xl text-gray-300">
              ${subscriptionPrice.price_usd} USD/mes
            </span>
          </div>
          <Button 
            className="rounded-full px-8 py-3 text-white font-bold"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            onClick={() => toast.info('Función de pago próximamente')}
          >
            Suscribirme Ahora
          </Button>
        </motion.div>

        {/* O comprar por carpeta */}
        <div className="text-center mb-8">
          <span className="text-gray-500">— O compra solo lo que necesitas —</span>
        </div>

        {/* Grid de carpetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {folders.map((folder, index) => {
            const access = userAccess[folder.id];
            const hasAccess = access?.has_access || folder.is_free;
            
            return (
              <motion.div
                key={folder.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleFolderClick(folder)}
                className="group cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: hasAccess ? '2px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={folder.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'} 
                    alt={folder.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  
                  {/* Badge de acceso */}
                  {hasAccess ? (
                    <Badge className="absolute top-3 right-3 bg-green-500 text-white">
                      <FaCheck className="mr-1" /> Acceso
                    </Badge>
                  ) : folder.is_free ? (
                    <Badge className="absolute top-3 right-3 bg-blue-500 text-white">
                      GRATIS
                    </Badge>
                  ) : (
                    <Badge className="absolute top-3 right-3 bg-[var(--brand-primary)] text-white">
                      <FaLock className="mr-1" /> Premium
                    </Badge>
                  )}
                  
                  {/* Contador de videos */}
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                    <FaPlay className="text-[#00F0FF]" />
                    <span className="text-sm font-medium">{folder.video_count || 0} videos</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[var(--brand-primary)] transition-colors">
                    {folder.name}
                  </h3>
                  {folder.description && (
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{folder.description}</p>
                  )}
                  
                  {/* Precio */}
                  {!hasAccess && !folder.is_free && folder.price > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-[#00F0FF]">
                        ${folder.price?.toLocaleString('es-CO')} COP
                      </span>
                      <Button 
                        size="sm"
                        className="rounded-full text-white"
                        style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.info('Función de compra próximamente');
                        }}
                      >
                        Comprar
                      </Button>
                    </div>
                  )}
                  
                  {hasAccess && (
                    <Button 
                      className="w-full rounded-full text-white"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                    >
                      <FaPlay className="mr-2" /> Ver Videos
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {folders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No hay carpetas de recetas disponibles aún.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recetarios;
